import vertexSource from "./shaders/stroke-stamps.vert";
import fragmentSource from "./shaders/stroke-stamps.frag";
import { createProgram } from "./program.js";

const TEXTURE_WIDTH = 256;
const FLOATS_PER_SEGMENT = 20;
const MAX_SEGMENTS = 65536;
const MAX_INSTANCES = 16777216;
const MAX_PRESSURE_TEXTURES = 64;
const IDENTITY = { a: 1, b: 0, c: 0, d: 1, x: 0, y: 0 };
const finite = (value, fallback) => Number.isFinite(value) ? value : fallback;

function prepareUpload(gl) {
  gl.bindBuffer(gl.PIXEL_UNPACK_BUFFER, null);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
  gl.pixelStorei(gl.UNPACK_IMAGE_HEIGHT, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
  gl.pixelStorei(gl.UNPACK_SKIP_IMAGES, 0);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
}

function textureParameters(gl) {
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
}

function pressureConfig(command) {
  const profile = command.profile;
  const lut = command.pressureLUT ?? profile.pressureLUT;
  const pressure = profile.pressure ?? [1, 1];
  if (lut) {
    // LUT values already contain the frontend's min_max mapping. Explicit
    // callback variation operates in normalized pressure space on the GPU;
    // omitted variation remains exactly zero, without legacy random defaults.
    const variation = pressure?.variation ?? {};
    const range = pressure?.min_max ?? [0, 1];
    return { mode: 3, lut, values: [1, 1, 1], gaussian: [0, 0, 1, 1],
      variation: [finite(variation.offset, 0), finite(variation.warp, 0), finite(variation.scale, 0), finite(variation.tilt, 0)],
      range: [Math.min(range[0], range[1]), Math.max(range[0], range[1])] };
  }
  if (Array.isArray(pressure) || ArrayBuffer.isView(pressure)) {
    const first = finite(pressure[0], 1), last = finite(pressure[pressure.length - 1], first);
    return { mode: 1, values: [first, pressure.length > 2 ? finite(pressure[1], first) : (first + last) / 2, last], gaussian: [0, 0, 1, 1] };
  }
  if (pressure && typeof pressure === "object") {
    if (typeof pressure.curve === "function") throw new TypeError("GPU stroke pressure callbacks must be sampled to command.pressureLUT.");
    return { mode: 2, values: [1, 1, 1], gaussian: [finite(pressure.curve?.[0], .15), finite(pressure.curve?.[1], .2), finite(pressure.min_max?.[0], 1), finite(pressure.min_max?.[1], 1)] };
  }
  const value = finite(pressure, 1);
  return { mode: 1, values: [value, value, value], gaussian: [0, 0, value, value] };
}

function sameMaterial(first, next) {
  return first.profile === next.profile && first.tipTexture === next.tipTexture &&
    first.pressureLUT === next.pressureLUT && first.clip === next.clip &&
    (first.resolution ?? 1) === (next.resolution ?? 1) && (first.alpha ?? 1) === (next.alpha ?? 1);
}

/**
 * Independent WebGL2 stroke generator. CPU work scales with path segments;
 * instanced vertex shaders generate all positions, pressure, grain and scatter.
 * The owning engine captures/restores GL state once around its render pass.
 */
export class GpuStroke {
  constructor(gl) {
    if (!gl || typeof gl.drawArraysInstanced !== "function") throw new TypeError("GpuStroke requires WebGL2.");
    this.gl = gl;
    this.program = createProgram(gl, vertexSource, fragmentSource);
    this.vao = gl.createVertexArray();
    this.segmentTexture = gl.createTexture();
    this.fallbackTip = gl.createTexture();
    this._lutTextures = new Map();
    this._data = new Float32Array(TEXTURE_WIDTH * 4);
    this._textureHeight = 0;
    this._destroyed = false;
    this.stats = { drawCalls: 0, segments: 0, instances: 0, uploadedBytes: 0 };
    this.uniforms = {};
    for (const name of ["segments", "pressureLUT", "segmentCount", "particles", "type", "rotate", "pressureMode", "pressureLUTWidth", "pressureVariation", "pressureRange", "markerTip", "viewport", "resolution", "profile", "ink", "pressure", "gaussian", "tip", "imageTip", "clip", "clipInverse", "clipBounds"]) {
      this.uniforms[name] = gl.getUniformLocation(this.program, `u_${name}`);
    }
    prepareUpload(gl);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.fallbackTip);
    textureParameters(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
  }

  resetStats() {
    for (const name of Object.keys(this.stats)) this.stats[name] = 0;
  }

  draw(command, maskTarget) {
    return this.drawBatch([command], maskTarget);
  }

  drawBatch(commands, maskTarget) {
    if (this._destroyed) throw new Error("GpuStroke has been destroyed.");
    if (!Array.isArray(commands)) throw new TypeError("GPU stroke commands must be an array.");
    if (!maskTarget?.framebuffer || !Number.isFinite(maskTarget.width) || !Number.isFinite(maskTarget.height) || maskTarget.width <= 0 || maskTarget.height <= 0) {
      throw new TypeError("GPU stroke maskTarget requires a framebuffer and positive physical width/height.");
    }
    let index = 0;
    while (index < commands.length) {
      let end = index + 1;
      let segmentCount = (commands[index]?.segments?.length ?? 0) / 6;
      while (end < commands.length && sameMaterial(commands[index], commands[end])) {
        const nextCount = (commands[end]?.segments?.length ?? 0) / 6;
        if (segmentCount + nextCount > MAX_SEGMENTS) break;
        segmentCount += nextCount;end++;
      }
      this._drawGroup(commands, index, end, maskTarget);
      index = end;
    }
    return this.stats;
  }

  _pressureTexture(lut) {
    const gl = this.gl;
    if (!lut) return { texture: this.fallbackTip, width: 1 };
    if (!ArrayBuffer.isView(lut) && !Array.isArray(lut)) throw new TypeError("pressureLUT must be an array of pressure samples.");
    if (!Number.isInteger(lut.length) || !lut.length || lut.length > 2048) throw new RangeError("pressureLUT requires between 1 and 2048 samples.");
    const cached = this._lutTextures.get(lut);
    if (cached && cached.width === lut.length && cached.values.every((value, i) => value === Math.fround(lut[i]))) {
      this._lutTextures.delete(lut);this._lutTextures.set(lut,cached);
      return cached;
    }
    const values = new Float32Array(lut.length);
    for (let i = 0; i < lut.length; i++) {
      if (!Number.isFinite(lut[i])) throw new TypeError("pressureLUT samples must be finite.");
      values[i] = lut[i];
    }
    const texture = cached?.texture ?? gl.createTexture();
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    textureParameters(gl);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, values.length, 1, 0, gl.RED, gl.FLOAT, values);
    const entry = { texture, width: values.length, values };
    this._lutTextures.delete(lut);
    this._lutTextures.set(lut, entry);
    if (this._lutTextures.size > MAX_PRESSURE_TEXTURES) {
      const oldest = this._lutTextures.keys().next().value;
      gl.deleteTexture(this._lutTextures.get(oldest).texture);
      this._lutTextures.delete(oldest);
    }
    this.stats.uploadedBytes += values.byteLength;
    return entry;
  }

  _drawGroup(commands, first, end, target) {
    const gl = this.gl;
    const command = commands[first];
    const profile = command.profile;
    if (!profile || typeof profile !== "object") throw new TypeError("GPU stroke commands require a profile.");
    const spacing = finite(profile.spacing, 1);
    if (spacing <= 0) throw new RangeError("GPU stroke spacing must be positive.");
    const pressure = pressureConfig(command);
    const type = profile.type === "spray" ? 1 : profile.type === "marker" ? 2 :
      (profile.type === "custom" || profile.type === "image") ? 3 : 0;
    let minimumPressure = pressure.mode === 3 ? Math.min(...pressure.lut) :
      pressure.mode === 2 ? Math.min(pressure.gaussian[2], pressure.gaussian[3]) : Math.min(...pressure.values);
    if (pressure.variation?.some(value => value !== 0)) minimumPressure = Math.min(minimumPressure, pressure.range[0]);
    let endpointMinimum = 1;
    for (let i = first; i < end; i++) if (commands[i].pressures) {
      for (const value of commands[i].pressures) endpointMinimum = Math.min(endpointMinimum, value);
    }
    minimumPressure *= endpointMinimum;
    const grain = Math.max(0, finite(profile.grain, 1));
    const particles = type === 1 ? Math.max(1, Math.ceil(grain / Math.max(.05, minimumPressure))) : 1;
    if (particles > 2048) throw new RangeError("GPU spray requires at most 2048 particles per path sample.");
    let requestedSegments = 0;
    for (let i = first; i < end; i++) {
      const segments = commands[i].segments;
      if (!(segments instanceof Float32Array) || segments.length % 6 !== 0) throw new TypeError("segments must be a Float32Array with stride 6.");
      requestedSegments += segments.length / 6;
    }
    if (requestedSegments > MAX_SEGMENTS) throw new RangeError(`A GPU stroke batch supports at most ${MAX_SEGMENTS} segments.`);
    let rows = 1;
    const requiredRows = Math.max(1, Math.ceil(requestedSegments * 5 / TEXTURE_WIDTH));
    while (rows < requiredRows) rows *= 2;
    if (this._data.length < rows * TEXTURE_WIDTH * 4) this._data = new Float32Array(rows * TEXTURE_WIDTH * 4);
    let segmentCount = 0, instances = 0;
    for (let i = first; i < end; i++) {
      const current = commands[i];
      const segments = current.segments;
      const count = segments.length / 6;
      const totalLength = finite(current.totalLength, count ? segments[segments.length - 2] + segments[segments.length - 1] : 0);
      if (totalLength <= 0) continue;
      const transform = current.transform ?? IDENTITY;
      const weight = finite(current.weight, 1);
      if (weight <= 0) continue;
      const seed = finite(current.seed, 1) >>> 0;
      for (let segment = 0; segment < count; segment++) {
        const offset = segment * 6;
        for (let k = 0; k < 6; k++) if (!Number.isFinite(segments[offset + k])) throw new TypeError("GPU stroke segment values must be finite.");
        const distance = segments[offset + 4], length = segments[offset + 5];
        if (length <= 0) continue;
        const firstStamp = Math.ceil(distance / spacing);
        const lastStamp = segment === count - 1 ? Math.ceil(totalLength / spacing) + 1 : Math.ceil((distance + length) / spacing);
        const stampCount = Math.max(0, lastStamp - firstStamp);
        if (!stampCount) continue;
        if (instances + stampCount * particles > MAX_INSTANCES) throw new RangeError(`A GPU stroke batch supports at most ${MAX_INSTANCES} particles.`);
        const destination = segmentCount * FLOATS_PER_SEGMENT;
        this._data.set(segments.subarray(offset, offset + 4), destination);
        this._data[destination + 4] = distance;
        this._data[destination + 5] = length;
        this._data[destination + 6] = firstStamp;
        this._data[destination + 7] = instances;
        this._data[destination + 8] = totalLength;
        this._data[destination + 9] = seed & 65535;
        this._data[destination + 10] = seed >>> 16;
        this._data[destination + 11] = weight;
        this._data[destination + 12] = finite(transform.a, 1);
        this._data[destination + 13] = finite(transform.b, 0);
        this._data[destination + 14] = finite(transform.c, 0);
        this._data[destination + 15] = finite(transform.d, 1);
        this._data[destination + 16] = finite(transform.x, 0);
        this._data[destination + 17] = finite(transform.y, 0);
        this._data[destination + 18] = finite(current.pressures?.[segment * 2], 1);
        this._data[destination + 19] = finite(current.pressures?.[segment * 2 + 1], 1);
        segmentCount++;
        instances += stampCount * particles;
      }
    }
    if (!instances) return;
    prepareUpload(gl);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindSampler(0, null);
    gl.bindTexture(gl.TEXTURE_2D, this.segmentTexture);
    if (this._textureHeight < rows) {
      textureParameters(gl);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, TEXTURE_WIDTH, rows, 0, gl.RGBA, gl.FLOAT, null);
      this._textureHeight = rows;
    }
    const uploadRows = Math.ceil(segmentCount * 5 / TEXTURE_WIDTH);
    const upload = this._data.subarray(0, uploadRows * TEXTURE_WIDTH * 4);
    gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, TEXTURE_WIDTH, uploadRows, gl.RGBA, gl.FLOAT, upload);
    const lut = this._pressureTexture(pressure.lut);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindSampler(2, null);
    gl.bindTexture(gl.TEXTURE_2D, lut.texture);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindSampler(1, null);
    gl.bindTexture(gl.TEXTURE_2D, command.tipTexture ?? this.fallbackTip);
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, Math.round(target.width), Math.round(target.height));
    gl.disable(gl.DEPTH_TEST); gl.disable(gl.STENCIL_TEST); gl.disable(gl.CULL_FACE);
    gl.disable(gl.SCISSOR_TEST); gl.disable(gl.RASTERIZER_DISCARD);
    gl.colorMask(true, true, true, true);
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ONE_MINUS_DST_ALPHA, gl.ONE);
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    const u = this.uniforms;
    gl.uniform1i(u.segments, 0); gl.uniform1i(u.tip, 1); gl.uniform1i(u.pressureLUT, 2);
    gl.uniform1i(u.segmentCount, segmentCount); gl.uniform1i(u.particles, particles);
    gl.uniform1i(u.type, type); gl.uniform1i(u.rotate, profile.rotate === "random" ? 2 : profile.rotate === "natural" ? 1 : 0);
    gl.uniform1i(u.pressureMode, pressure.mode); gl.uniform1i(u.pressureLUTWidth, lut.width);
    gl.uniform4f(u.pressureVariation, ...(pressure.variation ?? [0, 0, 0, 0]));
    gl.uniform2f(u.pressureRange, ...(pressure.range ?? [0, 1]));
    gl.uniform1i(u.markerTip, profile.markerTip !== false);
    gl.uniform2f(u.viewport, target.width, target.height);
    const resolution = finite(command.resolution, 1);
    if (resolution <= 0) throw new RangeError("GPU stroke resolution must be positive.");
    gl.uniform1f(u.resolution, resolution);
    gl.uniform4f(u.profile, Math.max(0, finite(profile.weight, 1)), spacing, finite(profile.scatter ?? profile.vibration, 0), finite(profile.sharpness ?? profile.definition, 1));
    gl.uniform4f(u.ink, grain, Math.max(0, finite(profile.opacity, 255)) / 255 * Math.max(0, finite(command.alpha, 1)), Math.max(0, finite(profile.noise, .3)), 0);
    gl.uniform3f(u.pressure, ...pressure.values); gl.uniform4f(u.gaussian, ...pressure.gaussian);
    gl.uniform1i(u.imageTip, type === 3);
    gl.uniform1i(u.clip, !!command.clip);
    const inverse = command.clip?.inverse ?? [1, 0, 0, 1, 0, 0];
    gl.uniformMatrix3fv(u.clipInverse, false, new Float32Array([inverse[0], inverse[1], 0, inverse[2], inverse[3], 0, inverse[4], inverse[5], 1]));
    const bounds = command.clip?.bounds ?? [0, 0, 0, 0];
    gl.uniform4f(u.clipBounds, Math.min(bounds[0], bounds[2]), Math.min(bounds[1], bounds[3]), Math.max(bounds[0], bounds[2]), Math.max(bounds[1], bounds[3]));
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, instances);
    this.stats.drawCalls++;
    this.stats.segments += segmentCount;
    this.stats.instances += instances;
    this.stats.uploadedBytes += upload.byteLength;
  }

  destroy() {
    if (this._destroyed) return;
    const gl = this.gl;
    gl.deleteVertexArray(this.vao);
    gl.deleteTexture(this.segmentTexture);
    gl.deleteTexture(this.fallbackTip);
    for (const entry of this._lutTextures.values()) gl.deleteTexture(entry.texture);
    this._lutTextures.clear();
    gl.deleteProgram(this.program);
    this._data = null;
    this._destroyed = true;
  }
}
